
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'react-hot-toast';
import { Plus, Pencil, Trash2, FileText, CheckCircle, GripVertical } from 'lucide-react';
import ConfirmDialog from '../components/ConfirmDialog';
import StepCard from '../components/StepCard';

const EAuctionGettingStartedPage = () => {
    const [loading, setLoading] = useState(true);

    // Settings State
    const [gettingStartedSettings, setGettingStartedSettings] = useState({
        section_title: '',
        section_description: ''
    });

    // Secure Bidding Settings State
    const [secureBiddingSettings, setSecureBiddingSettings] = useState({
        title: '',
        description: '',
        is_active: true
    });

    // Steps State
    const [steps, setSteps] = useState([]);
    const [isStepModalOpen, setIsStepModalOpen] = useState(false);
    const [stepModalData, setStepModalData] = useState({
        step_number: '',
        title: '',
        description: '',
        icon: 'FileText',
        display_order: 0,
        is_active: true
    });
    const [editingStep, setEditingStep] = useState(null);
    const [stepDeleteConfirm, setStepDeleteConfirm] = useState({ isOpen: false, id: null });

    const fetchData = async () => {
        try {
            setLoading(true);
            const [settingsResponse, stepsResponse, secureBiddingResponse] = await Promise.all([
                axios.get('/api/admin/e-auction-properties/settings/getting-started'),
                axios.get('/api/admin/e-auction-properties/steps'),
                axios.get('/api/admin/e-auction-properties/settings/secure-bidding')
            ]);

            if (settingsResponse.data.success && settingsResponse.data.data) {
                setGettingStartedSettings(settingsResponse.data.data);
            }
            if (stepsResponse.data.success) {
                setSteps(stepsResponse.data.data || []);
            }
            if (secureBiddingResponse.data.success && secureBiddingResponse.data.data) {
                setSecureBiddingSettings(secureBiddingResponse.data.data);
            }
        } catch (error) {
            console.error('Error fetching data:', error);
            toast.error('Failed to load data');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchData();
    }, []);

    const handleGettingStartedSettingsChange = (e) => {
        const { name, value } = e.target;
        setGettingStartedSettings(prev => ({ ...prev, [name]: value }));
    };

    const handleGettingStartedSettingsSubmit = async () => {
        try {
            await axios.put('/api/admin/e-auction-properties/settings/getting-started', gettingStartedSettings);
            toast.success('Getting Started section updated');
            fetchData();
        } catch (error) {
            console.error('Error updating settings:', error);
            toast.error('Failed to update settings');
        }
    };

    const handleSecureBiddingSettingsChange = (e) => {
        const { name, value, type, checked } = e.target;
        setSecureBiddingSettings(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value
        }));
    };

    const handleSecureBiddingSettingsSubmit = async () => {
        try {
            await axios.put('/api/admin/e-auction-properties/settings/secure-bidding', secureBiddingSettings);
            toast.success('Secure bidding settings updated');
            fetchData();
        } catch (error) {
            console.error('Error updating secure bidding settings:', error);
            toast.error('Failed to update settings');
        }
    };

    const handleAddStep = () => {
        setEditingStep(null);
        setStepModalData({
            step_number: '',
            title: '',
            description: '',
            icon: 'FileText',
            display_order: steps.length + 1,
            is_active: true
        });
        setIsStepModalOpen(true);
    };

    const handleEditStep = (step) => {
        console.log('Editing step:', step);
        setEditingStep(step);
        setStepModalData({
            ...step,
            is_active: Boolean(step.is_active) // Ensure boolean for checkbox
        });
        setIsStepModalOpen(true);
    };

    const handleStatusToggle = async (updatedStep) => {
        try {
            await axios.put(`/api/admin/e-auction-properties/steps/${updatedStep.id}`, updatedStep);
            toast.success('Step status updated');
            fetchData();
        } catch (error) {
            console.error('Error updating step status:', error);
            toast.error('Failed to update step status');
        }
    };

    const handleStepSubmit = async (e) => {
        e.preventDefault();
        try {
            if (editingStep) {
                await axios.put(`/api/admin/e-auction-properties/steps/${editingStep.id}`, stepModalData);
                toast.success('Step updated');
            } else {
                await axios.post('/api/admin/e-auction-properties/steps', stepModalData);
                toast.success('Step created');
            }
            setIsStepModalOpen(false);
            fetchData();
        } catch (error) {
            console.error('Error saving step:', error);
            toast.error('Failed to save step');
        }
    };

    const confirmDeleteStep = async () => {
        try {
            await axios.delete(`/api/admin/e-auction-properties/steps/${stepDeleteConfirm.id}`);
            toast.success('Step deleted');
            setStepDeleteConfirm({ isOpen: false, id: null });
            fetchData();
        } catch (error) {
            console.error('Error deleting step:', error);
            toast.error('Failed to delete step');
        }
    };

    if (loading) return <div className="p-8 text-center">Loading...</div>;

    return (
        <div className="max-w-7xl mx-auto">
            <div className="flex justify-between items-center mb-8">
                <div>
                    <h1 className="text-2xl font-bold text-slate-800">Getting Started Section (v2)</h1>
                    <p className="text-slate-500 mt-1">Manage the "How to Participate" steps and settings</p>
                </div>
            </div>

            {/* Getting Started Section Settings */}
            <div className="bg-white rounded-2xl p-6 shadow-sm border border-slate-100 mb-8">
                <div className="flex justify-between items-center mb-6">
                    <h2 className="text-lg font-bold text-slate-800">Section Header Settings</h2>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                    <div>
                        <label className="block text-sm font-semibold text-slate-700 mb-2">Section Title</label>
                        <input
                            type="text"
                            name="section_title"
                            value={gettingStartedSettings.section_title || ''}
                            onChange={handleGettingStartedSettingsChange}
                            className="w-full px-4 py-2.5 border border-slate-300 rounded-xl focus:ring-2 focus:ring-primary-500 outline-none transition-all"
                        />
                    </div>
                    <div>
                        <label className="block text-sm font-semibold text-slate-700 mb-2">Section Description</label>
                        <input
                            type="text"
                            name="section_description"
                            value={gettingStartedSettings.section_description || ''}
                            onChange={handleGettingStartedSettingsChange}
                            className="w-full px-4 py-2.5 border border-slate-300 rounded-xl focus:ring-2 focus:ring-primary-500 outline-none transition-all"
                        />
                    </div>
                </div>
                <div className="flex justify-end">
                    <button
                        onClick={handleGettingStartedSettingsSubmit}
                        className="px-6 py-2.5 bg-primary-900 text-white rounded-xl font-semibold hover:bg-primary-800 transition-all shadow-md"
                    >
                        Save Settings
                    </button>
                </div>
            </div>

            {/* Secure Bidding Settings */}
            <div className="bg-white rounded-2xl p-6 shadow-sm border border-slate-100 mb-8">
                <div className="flex justify-between items-center mb-6">
                    <h2 className="text-lg font-bold text-slate-800">Secure Bidding Section Settings</h2>
                </div>

                <div className="grid grid-cols-1 gap-6 mb-6">
                    <div>
                        <label className="block text-sm font-semibold text-slate-700 mb-2">Title</label>
                        <input
                            type="text"
                            name="title"
                            value={secureBiddingSettings.title || ''}
                            onChange={handleSecureBiddingSettingsChange}
                            className="w-full px-4 py-2.5 border border-slate-300 rounded-xl focus:ring-2 focus:ring-primary-500 outline-none transition-all"
                            placeholder="Secure & Transparent Bidding"
                        />
                    </div>
                    <div>
                        <label className="block text-sm font-semibold text-slate-700 mb-2">Description</label>
                        <textarea
                            name="description"
                            value={secureBiddingSettings.description || ''}
                            onChange={handleSecureBiddingSettingsChange}
                            className="w-full px-4 py-2.5 border border-slate-300 rounded-xl focus:ring-2 focus:ring-primary-500 outline-none transition-all h-24 resize-none"
                            placeholder="All bids are recorded and timestamped electronically..."
                        />
                    </div>
                    <div>
                        <label className="flex items-center gap-3 cursor-pointer">
                            <input
                                type="checkbox"
                                name="is_active"
                                checked={Boolean(secureBiddingSettings.is_active)}
                                onChange={handleSecureBiddingSettingsChange}
                                className="w-5 h-5 text-primary-600 border-slate-300 rounded focus:ring-2 focus:ring-primary-500"
                            />
                            <span className="text-sm font-semibold text-slate-700">Show on public page</span>
                        </label>
                    </div>
                </div>
                <div className="flex justify-end">
                    <button
                        onClick={handleSecureBiddingSettingsSubmit}
                        className="px-6 py-2.5 bg-primary-900 text-white rounded-xl font-semibold hover:bg-primary-800 transition-all shadow-md"
                    >
                        Save Settings
                    </button>
                </div>
            </div>

            {/* Steps Management */}
            <div>
                <div className="flex justify-between items-center mb-6">
                    <div>
                        <h2 className="text-xl font-bold text-slate-800">Participation Steps</h2>
                        <p className="text-slate-500 text-sm mt-1">Manage the steps displayed on the public page</p>
                    </div>
                    <button
                        onClick={handleAddStep}
                        className="flex items-center gap-2 bg-primary-600 text-white px-4 py-2 rounded-xl hover:bg-primary-700 transition-colors shadow-md font-semibold"
                    >
                        <Plus size={18} /> Add Step
                    </button>
                </div>

                {steps.length === 0 ? (
                    <div className="bg-white rounded-2xl p-12 text-center border-2 border-dashed border-slate-200">
                        <div className="w-16 h-16 bg-slate-50 rounded-full flex items-center justify-center mx-auto mb-4">
                            <FileText className="text-slate-300" size={32} />
                        </div>
                        <h3 className="text-lg font-bold text-slate-800 mb-2">No steps added yet</h3>
                        <p className="text-slate-500 mb-6 max-w-sm mx-auto">Define the steps users need to follow to participate in the e-auction.</p>
                        <button
                            onClick={handleAddStep}
                            className="text-primary-600 font-semibold hover:text-primary-700"
                        >
                            Add your first step
                        </button>
                    </div>
                ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                        {steps.map((step) => (
                            <StepCard
                                key={step.id}
                                step={step}
                                onEdit={handleEditStep}
                                onDelete={(id) => setStepDeleteConfirm({ isOpen: true, id })}
                                onToggle={handleStatusToggle}
                            />
                        ))}
                    </div>
                )}
            </div>

            {/* Step Modal */}
            {isStepModalOpen && (
                <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
                    <div className="bg-white rounded-2xl w-full max-w-lg p-6 animate-in zoom-in duration-200">
                        <div className="flex justify-between items-center mb-6">
                            <h2 className="text-xl font-bold text-slate-800">{editingStep ? 'Edit Step' : 'Add New Step'}</h2>
                            <button onClick={() => setIsStepModalOpen(false)} className="text-slate-400 hover:text-slate-600">
                                <Plus className="rotate-45" size={24} />
                            </button>
                        </div>

                        <form onSubmit={handleStepSubmit} className="space-y-4">
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-sm font-semibold text-slate-700 mb-1">Step Number</label>
                                    <input
                                        type="text"
                                        value={stepModalData.step_number}
                                        onChange={(e) => setStepModalData({ ...stepModalData, step_number: e.target.value })}
                                        className="w-full px-4 py-2 border border-slate-300 rounded-xl focus:ring-2 focus:ring-primary-500 outline-none"
                                        placeholder="01"
                                        required
                                    />
                                </div>
                                <div className="flex flex-col">
                                    <label className="block text-sm font-semibold text-slate-700 mb-2">Status</label>
                                    <label className="relative inline-flex items-center cursor-pointer">
                                        <input
                                            type="checkbox"
                                            checked={stepModalData.is_active}
                                            onChange={(e) => setStepModalData({ ...stepModalData, is_active: e.target.checked })}
                                            className="sr-only peer"
                                        />
                                        <div className="w-11 h-6 bg-slate-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-primary-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-slate-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary-600"></div>
                                        <span className="ml-3 text-sm font-medium text-slate-700">{stepModalData.is_active ? 'Active' : 'Inactive'}</span>
                                    </label>
                                </div>
                                <div>
                                    <label className="block text-sm font-semibold text-slate-700 mb-1">Icon Name</label>
                                    <select
                                        value={stepModalData.icon}
                                        onChange={(e) => setStepModalData({ ...stepModalData, icon: e.target.value })}
                                        className="w-full px-4 py-2 border border-slate-300 rounded-xl focus:ring-2 focus:ring-primary-500 outline-none"
                                    >
                                        <option value="Eye">Eye</option>
                                        <option value="FileText">FileText</option>
                                        <option value="IndianRupee">IndianRupee</option>
                                        <option value="User">User</option>
                                        <option value="Gavel">Gavel</option>
                                        <option value="Shield">Shield</option>
                                    </select>
                                </div>
                            </div>

                            <div>
                                <label className="block text-sm font-semibold text-slate-700 mb-1">Title</label>
                                <input
                                    type="text"
                                    value={stepModalData.title}
                                    onChange={(e) => setStepModalData({ ...stepModalData, title: e.target.value })}
                                    className="w-full px-4 py-2 border border-slate-300 rounded-xl focus:ring-2 focus:ring-primary-500 outline-none"
                                    required
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-semibold text-slate-700 mb-1">Description</label>
                                <textarea
                                    value={stepModalData.description}
                                    onChange={(e) => setStepModalData({ ...stepModalData, description: e.target.value })}
                                    className="w-full px-4 py-2 border border-slate-300 rounded-xl focus:ring-2 focus:ring-primary-500 outline-none h-24 resize-none"
                                    required
                                />
                            </div>

                            <div className="flex justify-end gap-3 pt-6 border-t border-slate-100">
                                <button
                                    type="button"
                                    onClick={() => setIsStepModalOpen(false)}
                                    className="px-6 py-2.5 border border-slate-300 text-slate-600 rounded-xl font-semibold hover:bg-slate-50 transition-all"
                                >
                                    Cancel
                                </button>
                                <button
                                    type="submit"
                                    className="px-6 py-2.5 bg-primary-600 text-white rounded-xl font-semibold hover:bg-primary-700 transition-all shadow-lg"
                                >
                                    {editingStep ? 'Update Step' : 'Create Step'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            <ConfirmDialog
                isOpen={stepDeleteConfirm.isOpen}
                onClose={() => setStepDeleteConfirm({ isOpen: false, id: null })}
                onConfirm={confirmDeleteStep}
                title="Delete Step"
                message="Are you sure you want to delete this step? This action cannot be undone."
            />
        </div>
    );
};

export default EAuctionGettingStartedPage;
